/**
 * main.ts
 *
 * Bootstraps Vuetify and other plugins then mounts the App`
 */

// Components
import App from './App.vue'

// Composables
import { createApp } from 'vue'

// Plugins
import { registerPlugins } from '@/plugins'

// AWS Amplify
import { startSessionHandoff } from '@/utils/sessionHandoff'
import { configureAmplifyAtBoot } from '@/utils/cognitoPoolConfig'

// Auth0
import { createAuth0 } from '@auth0/auth0-vue'

// Sentry
import * as Sentry from "@sentry/vue";
import router from "./router";
import { setAuth0Client } from '@/utils/auth0Client'
import { installUserOptionsFallback } from '@/infrastructure/user/installUserOptionsFallback'

// i18n
import i18n from './i18n'

// Before registerPlugins: the router's first guard awaits this redemption.
startSessionHandoff()

installUserOptionsFallback()

// Before createApp. Staff pool while this origin holds a staff session (magic login), home pool otherwise.
configureAmplifyAtBoot()

const app = createApp(App)

// registerPlugins already installs i18n, router, pinia, vuetify
registerPlugins(app)

// Configure Auth0
const redirectUri = `${window.location.origin}/auth/auth0-callback/`
const auth0Plugin = createAuth0({
    domain: import.meta.env.VITE_AUTH0_DOMAIN,
    clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
    authorizationParams: {
      redirect_uri: redirectUri,
      audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      scope: 'openid profile email offline_access',
    },
    useRefreshTokens: true,
    cacheLocation: 'localstorage',
  })
app.use(auth0Plugin)
// Exponer instancia del SDK para consumo desde utils/api.ts
setAuth0Client((app.config.globalProperties as any).$auth0)

// Initialize Sentry only in production to avoid noise locally
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "production",
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.captureConsoleIntegration({ levels: ["error"] }),
    ],
    tracesSampleRate: 0.2,
    tracePropagationTargets: [/^https:\/\/finnecto\.com\//],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });

  // Replay's startup DOM snapshot is a main-thread long task that scales with DOM
  // size, so it blocks LCP on heavy views. Add it once the browser is idle instead
  // of during init — costs the first few seconds of the replay, not the page load.
  const addReplay = () =>
    Sentry.getClient()?.addIntegration(Sentry.replayIntegration());
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(addReplay, { timeout: 5000 });
  } else {
    window.addEventListener("load", () => setTimeout(addReplay, 2000));
  }
}

// Handle chunk loading errors globally (catches component-level lazy loading via defineAsyncComponent)
// This catches errors that router.onError doesn't catch (non-route dynamic imports)
// Only runs on actual unhandled promise rejections - zero performance impact during normal operation
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  const errorMessage = error?.message || '';
  const errorStack = error?.stack || '';

  // Check if this is a chunk loading error (404 when old chunk requested after deployment)
  // Must be specific to JS chunk files to avoid catching API 404s or other errors
  const isChunkError =
    /Failed to fetch dynamically imported module/i.test(errorMessage) ||
    /Loading chunk [\d]+ failed/i.test(errorMessage) ||
    /ChunkLoadError/i.test(errorMessage) ||
    (/net::ERR_ABORTED.*404/i.test(errorMessage) && /\.js/i.test(errorMessage + errorStack)) ||
    (/404.*Not Found/i.test(errorStack) && /\.js/i.test(errorStack)) ||
    (error?.status === 404 && errorMessage.includes('.js'));

  if (isChunkError) {
    // Prevent default error handling and console spam
    event.preventDefault();

    // New deployment happened while user has old version open
    // Preserve intended target route across the reload (same logic as router.onError)
    try {
      const intended = sessionStorage.getItem('fto:intendedPath');
      const fallback = window.location.pathname + window.location.search + window.location.hash;
      const targetPath = intended || fallback;
      sessionStorage.setItem('fto:postReloadPath', targetPath);
    } catch { /* no-op */ }

    // Reload to get the new version - this is the standard industry solution
    window.location.reload();
  }
});

app.mount('#app')

// After mount, if a post-reload path was saved, restore it and clear flags
try {
  const target = sessionStorage.getItem('fto:postReloadPath');
  if (target && router.currentRoute.value.fullPath !== target) {
    router.replace(target).finally(() => {
      try {
        sessionStorage.removeItem('fto:postReloadPath');
        sessionStorage.removeItem('fto:intendedPath');
      } catch { /* no-op */ }
    });
  }
} catch { /* no-op */ }

// Remove the boot splash (index.html) once the initial navigation resolves. router.isReady()
// covers the whole blank window: the blocking guard and the first view's lazy chunk.
const bootLoaderStart = performance.now()
const BOOT_LOADER_MIN_MS = 400 // min on-screen time so the splash doesn't flash on warm loads

const releaseScrollLock = () => document.documentElement.classList.remove('app-booting')

const removeBootLoader = () => {
  const loader = document.getElementById('app-loader')
  if (!loader) {
    releaseScrollLock() // already gone — just make sure scroll is unlocked
    return
  }
  loader.classList.add('is-leaving')
  const cleanup = () => {
    loader.remove()
    releaseScrollLock() // release after the fade so the splash can't drift while the app behind overflows
  }
  const fallbackTimer = window.setTimeout(cleanup, 600) // fallback if transitionend never fires
  loader.addEventListener('transitionend', () => {
    window.clearTimeout(fallbackTimer)
    cleanup()
  }, { once: true })
}

const scheduleBootLoaderRemoval = () => {
  const wait = Math.max(0, BOOT_LOADER_MIN_MS - (performance.now() - bootLoaderStart))
  window.setTimeout(removeBootLoader, wait)
}

// .finally: remove even if the initial navigation rejects (e.g. session error)
router.isReady().finally(scheduleBootLoaderRemoval)
window.setTimeout(removeBootLoader, 10000) // safety net: never trap the user behind the splash
